import { parse as parseYaml } from 'yaml';

/**
 * What a component's API tab reads from an OpenAPI definition
 * (specs/api-docs.md): what the API is, where it answers, what a caller needs
 * to be let in, what it can do, and the types it speaks — each read from the
 * definition and left out where the definition says nothing.
 *
 * A definition is a third party's document: any part of it may be missing,
 * malformed, recursive or huge. Reading it never throws — a definition that
 * cannot be read is `undefined`, which the tab says in words — and nothing in
 * it is expanded without a bound.
 */

export type AuthScheme = {
  /** The key under components.securitySchemes. */
  name: string;
  kind: 'bearer' | 'basic' | 'apiKey' | 'oauth2' | 'openIdConnect' | 'other';
  /** "Bearer", "API key in header X-Api-Key", "OAuth 2" … */
  label: string;
  description?: string;
  /** For an API key: where it goes and under which name. */
  in?: 'header' | 'query' | 'cookie';
  paramName?: string;
  /** Asked for by the contract, but never declared in it. */
  undeclared?: boolean;
};

/** A parameter a caller must send, other than one in the path. */
export type Parameter = { name: string; in: 'query' | 'header' | 'cookie' };

export type Body = {
  mediaType: string;
  /** Undefined for a body the sample cannot write — a file upload, say. */
  value?: unknown;
  from: 'example' | 'schema';
};

export type Operation = {
  method: string;
  path: string;
  summary?: string;
  tags: string[];
  /**
   * The schemes one call of this operation sends together — its first
   * security requirement; [] when it is open; undefined when it defers to the
   * API's.
   */
  requirement?: string[];
  /** Every scheme this operation names, alternatives included. */
  securityNames?: string[];
  /** The query, header and cookie parameters it requires. */
  parameters: Parameter[];
  /** Whether it takes a request body at all. */
  hasBody: boolean;
  /** The body a caller sends — worked out for the sample operation only. */
  body?: Body;
};

export type SchemaField = { name: string; type: string; required: boolean };
export type SchemaType = {
  name: string;
  fields: SchemaField[];
  /** What a type without fields of its own is: "string · one of: a, b". */
  shape?: string;
};

/**
 * How the needed schemes relate: any one of them will do; which one depends
 * on the operation; or authenticating is optional.
 */
export type AuthNote = 'any' | 'byOperation' | 'optional';

export type ApiSpec = {
  /** "OpenAPI 3.1", "Swagger 2.0". */
  format: string;
  title?: string;
  version?: string;
  /** The first server a caller would use, its variables at their defaults. */
  server?: string;
  /** The schemes a caller needs — see authOf. */
  auth: AuthScheme[];
  authNote?: AuthNote;
  /**
   * Where the contract offers alternatives and one of them asks for several
   * schemes together: each alternative, in order.
   */
  authGroups?: AuthScheme[][];
  /** Every scheme the definition declares, by name. */
  schemes: Record<string, AuthScheme>;
  /** What one call sends together by default: the first global requirement. */
  requirement?: string[];
  operations: Operation[];
  schemas: SchemaType[];
  /** The operation the code sample is written for — see sampleOperationOf. */
  sample?: Operation;
};

type Json = Record<string, unknown>;

const METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

const isObject = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** A string, or a number YAML read unquoted ("version: 1.0"). */
const textOf = (v: unknown): string | undefined => {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return undefined;
};

/**
 * A value as the definition writes it. YAML reads `version: 2.10` as the
 * number 2.1 and `openapi: 3.0` as 3, so a number is taken from the source
 * text instead — where the facts bar reads the version too.
 */
function sourceTextOf(
  source: string,
  key: 'openapi' | 'swagger' | 'version',
  value: unknown,
): string | undefined {
  if (typeof value !== 'number') return textOf(value);
  const indent = key === 'version' ? '[ \\t]+' : '';
  const match = new RegExp(`^${indent}${key}:[ \\t]*([^\\s#]+)`, 'm').exec(
    source,
  );
  return match?.[1] ?? String(value);
}

/** Read a definition as the catalog holds it — a JSON or YAML string. */
export function readDefinition(definition: unknown): Json | undefined {
  if (typeof definition !== 'string' || !definition.trim()) return undefined;
  try {
    // JSON is YAML too, but a JSON parser reads a large one far faster.
    const doc: unknown = definition.trim().startsWith('{')
      ? JSON.parse(definition)
      : parseYaml(definition);
    return isObject(doc) ? doc : undefined;
  } catch {
    return undefined;
  }
}

/** Follow a local "#/…" reference, once; anything else as it is. */
function resolve(doc: Json, value: unknown): unknown {
  if (!isObject(value) || typeof value.$ref !== 'string') return value;
  if (!value.$ref.startsWith('#/')) return value;
  const path = value.$ref.slice(2).split('/');
  let target: unknown = doc;
  for (const part of path) {
    target = isObject(target) ? target[part] : undefined;
  }
  return target ?? value;
}

/** A copy of a value, cut where it repeats or runs deep — an example may do both. */
function bounded(value: unknown, depth = 0, seen = new WeakSet()): unknown {
  if (typeof value !== 'object' || value === null) return value;
  if (depth > 8 || seen.has(value)) return Array.isArray(value) ? [] : {};
  seen.add(value);
  const copy = Array.isArray(value)
    ? value.slice(0, 20).map(v => bounded(v, depth + 1, seen))
    : Object.fromEntries(
        Object.entries(value)
          .slice(0, 50)
          .map(([k, v]) => [k, bounded(v, depth + 1, seen)]),
      );
  seen.delete(value);
  return copy;
}

function schemeOf(name: string, raw: unknown): AuthScheme {
  const s = isObject(raw) ? raw : {};
  const description =
    typeof s.description === 'string' && s.description.trim()
      ? s.description.trim()
      : undefined;
  const type = String(s.type ?? '').toLowerCase();
  const scheme = String(s.scheme ?? '').toLowerCase();
  if (type === 'http' && scheme === 'bearer') {
    return { name, kind: 'bearer', label: 'Bearer', description };
  }
  if ((type === 'http' && scheme === 'basic') || type === 'basic') {
    return { name, kind: 'basic', label: 'HTTP basic', description };
  }
  if (type === 'apikey') {
    const where = (['header', 'query', 'cookie'] as const).find(
      w => w === s.in,
    );
    const paramName = typeof s.name === 'string' ? s.name : undefined;
    return {
      name,
      kind: 'apiKey',
      label: `API key${where ? ` in ${where}` : ''}${
        paramName ? ` ${paramName}` : ''
      }`,
      description,
      in: where,
      paramName,
    };
  }
  if (type === 'oauth2') {
    return { name, kind: 'oauth2', label: 'OAuth 2', description };
  }
  if (type === 'openidconnect') {
    return {
      name,
      kind: 'openIdConnect',
      label: 'OpenID Connect',
      description,
    };
  }
  return { name, kind: 'other', label: type || name, description };
}

/** The scheme names of a security requirement list, alternatives included. */
function namesIn(security: unknown): string[] | undefined {
  if (!Array.isArray(security)) return undefined;
  return [
    ...new Set(
      security.flatMap(req => (isObject(req) ? Object.keys(req) : [])),
    ),
  ];
}

/**
 * The schemes of the first requirement in a list: sent together by one call,
 * where the later entries are alternatives to it. [] for an open operation.
 */
function firstRequirement(security: unknown): string[] | undefined {
  if (!Array.isArray(security)) return undefined;
  const [first] = security;
  return isObject(first) ? Object.keys(first) : [];
}

/**
 * The schemes a caller needs (decided 29 Sept, DX-303): those the
 * definition's own `security` asks for. Where it asks for none at the top —
 * absent, or open with the operations asking — those its operations ask for,
 * which then depend on the operation. Only a definition that states no
 * security anywhere falls back to every scheme it declares.
 */
function authOf(
  doc: Json,
  schemes: Record<string, AuthScheme>,
  operations: Operation[],
): { auth: AuthScheme[]; authNote?: AuthNote; authGroups?: AuthScheme[][] } {
  // A scheme asked for but never declared is named as such, not dropped: it
  // is the contract's own gap, and a row without it would read as open.
  const pick = (names: string[]) =>
    names.map(
      name =>
        schemes[name] ?? {
          name,
          kind: 'other' as const,
          label: name,
          undeclared: true,
        },
    );
  const global = Array.isArray(doc.security) ? doc.security : undefined;
  const globalNames = namesIn(global) ?? [];
  if (globalNames.length) {
    const optional = global!.some(
      req => isObject(req) && !Object.keys(req).length,
    );
    const requirements = global!.filter(
      (req): req is Json => isObject(req) && Object.keys(req).length > 0,
    );
    const choice = requirements.length > 1;
    let authNote: AuthNote | undefined;
    if (optional) authNote = 'optional';
    else if (choice) authNote = 'any';
    // "Any one of a, b, c" misreads [{a, b}, {c}]: a and b go together.
    const grouped = requirements.some(req => Object.keys(req).length > 1);
    return {
      auth: pick(globalNames),
      authNote,
      authGroups:
        choice && grouped
          ? requirements.map(req => pick(Object.keys(req)))
          : undefined,
    };
  }
  const byOperation = [
    ...new Set(operations.flatMap(op => op.securityNames ?? [])),
  ];
  if (byOperation.length) {
    return { auth: pick(byOperation), authNote: 'byOperation' };
  }
  // `security: []` at the top says the API is open; no word at all is not.
  return { auth: global ? [] : Object.values(schemes) };
}

// A path with a segment like these is how a service is watched or described,
// not what it is for — /health, /api/health/search-queue, /openapi.json, /docs.
const NOT_A_CAPABILITY =
  /^(health|healthz|healthcheck|ready|readyz|readiness|live|livez|liveness|ping|metrics|docs|redoc|openapi(\.json|\.ya?ml)?|swagger(\.json|\.ya?ml)?)$/i;

/**
 * The operation the code sample is written for: the first that is not a
 * health, readiness, metrics or spec endpoint — many contracts open on one —
 * else simply the first (decided 29 Sept, DX-303).
 */
export function sampleOperationOf(
  operations: Operation[],
  apiAsksForAuth = false,
): Operation | undefined {
  const watches = (path: string) =>
    path.split('/').some(segment => NOT_A_CAPABILITY.test(segment));
  // An operation that opts out of the API's own security — a webhook that
  // checks a signature instead, a public probe — is not how a caller uses it.
  const optsOut = (op: Operation) =>
    apiAsksForAuth && op.requirement !== undefined && !op.requirement.length;
  return (
    operations.find(op => !watches(op.path) && !optsOut(op)) ??
    operations.find(op => !watches(op.path)) ??
    operations[0]
  );
}

const PLACEHOLDERS: Record<string, unknown> = {
  integer: 0,
  number: 0,
  boolean: false,
};

/** How many values one skeleton may hold before the rest is left out. */
const SKELETON_BUDGET = 200;

/**
 * A body built from a schema: its required fields — all of them where it
 * requires none — each with a placeholder of its type. Nothing is invented: a
 * string is "<string>", or its format ("<date-time>"), or the first value its
 * enum allows. A type that contains itself is written once, and the whole
 * skeleton holds at most a couple of hundred values — a schema can otherwise
 * describe a body larger than memory.
 */
export function skeletonOf(doc: Json, schema: unknown): unknown {
  let budget = SKELETON_BUDGET;
  const build = (
    value: unknown,
    onPath: Set<string>,
    depth: number,
  ): unknown => {
    budget -= 1;
    const ref =
      isObject(value) && typeof value.$ref === 'string'
        ? value.$ref
        : undefined;
    if (budget < 0 || depth > 6 || (ref && onPath.has(ref))) return {};
    const path = ref ? new Set(onPath).add(ref) : onPath;
    const s = resolve(doc, value);
    if (!isObject(s)) return {};
    const all = (s.allOf ?? s.oneOf ?? s.anyOf) as unknown[] | undefined;
    if (Array.isArray(all) && all.length) {
      if (s.allOf) {
        return Object.assign(
          {},
          ...all.map(part => {
            const built = build(part, path, depth + 1);
            return isObject(built) ? built : {};
          }),
        );
      }
      return build(all[0], path, depth + 1);
    }
    if (Array.isArray(s.enum) && s.enum.length) return bounded(s.enum[0]);
    const type = Array.isArray(s.type)
      ? s.type.find(t => t !== 'null')
      : s.type;
    if (type === 'array') return [build(s.items, path, depth + 1)];
    if (type === 'object' || isObject(s.properties)) {
      const properties = isObject(s.properties) ? s.properties : {};
      const required = Array.isArray(s.required)
        ? (s.required as unknown[]).filter(
            (r): r is string => typeof r === 'string',
          )
        : [];
      const names = required.length ? required : Object.keys(properties);
      return Object.fromEntries(
        names.map(n => [n, build(properties[n], path, depth + 1)]),
      );
    }
    if (typeof type === 'string' && type in PLACEHOLDERS) {
      return PLACEHOLDERS[type];
    }
    const format = typeof s.format === 'string' ? s.format : undefined;
    return `<${format ?? 'string'}>`;
  };
  return build(schema, new Set(), 0);
}

/** Whether a media type is one the samples can write as JSON. */
const isJson = (mediaType: string) => /json/i.test(mediaType);

/**
 * The body a caller sends: the definition's example, else a skeleton of its
 * schema. A body of another kind — a file upload, a form — is named and left
 * out, rather than written as JSON the API would refuse.
 */
function bodyOf(doc: Json, operation: Json): Body | undefined {
  const requestBody = resolve(doc, operation.requestBody);
  if (isObject(requestBody) && isObject(requestBody.content)) {
    const content = requestBody.content;
    const mediaType =
      Object.keys(content).find(isJson) ?? Object.keys(content)[0];
    if (!mediaType) return undefined;
    if (!isJson(mediaType)) return { mediaType, from: 'schema' };
    const media = isObject(content[mediaType]) ? content[mediaType] : {};
    if (media.example !== undefined) {
      return { mediaType, value: bounded(media.example), from: 'example' };
    }
    if (isObject(media.examples)) {
      const first = resolve(doc, Object.values(media.examples)[0]);
      if (isObject(first) && first.value !== undefined) {
        return { mediaType, value: bounded(first.value), from: 'example' };
      }
    }
    const schema = resolve(doc, media.schema);
    if (isObject(schema) && schema.example !== undefined) {
      return { mediaType, value: bounded(schema.example), from: 'example' };
    }
    return { mediaType, value: skeletonOf(doc, media.schema), from: 'schema' };
  }
  // Swagger 2 carries the body as a parameter.
  const parameters = Array.isArray(operation.parameters)
    ? operation.parameters.map(p => resolve(doc, p))
    : [];
  const inBody = parameters.find(p => isObject(p) && p.in === 'body');
  if (isObject(inBody)) {
    const consumes = Array.isArray(doc.consumes) ? doc.consumes : [];
    const mediaType =
      typeof consumes[0] === 'string' ? consumes[0] : 'application/json';
    if (!isJson(mediaType)) return { mediaType, from: 'schema' };
    return { mediaType, value: skeletonOf(doc, inBody.schema), from: 'schema' };
  }
  const form = parameters.some(p => isObject(p) && p.in === 'formData');
  return form
    ? { mediaType: 'multipart/form-data', from: 'schema' }
    : undefined;
}

function hasBodyOf(doc: Json, operation: Json): boolean {
  if (operation.requestBody !== undefined) return true;
  return (
    Array.isArray(operation.parameters) &&
    operation.parameters.some(p => {
      const param = resolve(doc, p);
      return (
        isObject(param) && (param.in === 'body' || param.in === 'formData')
      );
    })
  );
}

/** The query, header and cookie parameters a call must carry. */
function parametersOf(doc: Json, item: Json, operation: Json): Parameter[] {
  const all = [
    ...(Array.isArray(item.parameters) ? item.parameters : []),
    ...(Array.isArray(operation.parameters) ? operation.parameters : []),
  ].map(p => resolve(doc, p));
  const byKey = new Map<string, Parameter>();
  for (const p of all) {
    if (!isObject(p) || typeof p.name !== 'string') continue;
    const where = (['query', 'header', 'cookie'] as const).find(
      w => w === p.in,
    );
    const key = `${p.in}:${p.name.toLowerCase()}`;
    // The operation's own declaration of a parameter overrides the path's.
    if (where && p.required === true)
      byKey.set(key, { name: p.name, in: where });
    else byKey.delete(key);
  }
  return [...byKey.values()];
}

function typeOf(doc: Json, schema: unknown, depth = 0): string {
  if (isObject(schema) && typeof schema.$ref === 'string') {
    return schema.$ref.split('/').pop() ?? 'object';
  }
  const s = isObject(schema) ? schema : {};
  if (depth > 5) return 'object';
  const type = Array.isArray(s.type)
    ? s.type.filter(t => t !== 'null').join(' | ')
    : s.type;
  if (type === 'array') return `${typeOf(doc, s.items, depth + 1)}[]`;
  if (typeof type === 'string' && type) return type;
  const all = (s.allOf ?? s.oneOf ?? s.anyOf) as unknown[] | undefined;
  if (Array.isArray(all)) {
    return all.map(part => typeOf(doc, part, depth + 1)).join(' | ');
  }
  return 'object';
}

function schemasOf(doc: Json): SchemaType[] {
  const components = isObject(doc.components) ? doc.components : {};
  let schemas: Json = {};
  if (isObject(components.schemas)) schemas = components.schemas;
  else if (isObject(doc.definitions)) schemas = doc.definitions;
  return Object.entries(schemas).map(([name, raw]) => {
    const s = isObject(raw) ? raw : {};
    const properties = isObject(s.properties) ? s.properties : {};
    const required = new Set(
      Array.isArray(s.required) ? (s.required as unknown[]) : [],
    );
    const fields = Object.entries(properties).map(([field, schema]) => ({
      name: field,
      type: typeOf(doc, schema),
      required: required.has(field),
    }));
    return { name, fields, shape: fields.length ? undefined : shapeOf(doc, s) };
  });
}

/**
 * A type with no properties is still something: an enum, a choice between
 * other types, or a map. Sixteen of the portal's 376 types were one of these
 * on 29 Sept.
 */
function shapeOf(doc: Json, s: Json): string {
  const enumValues = Array.isArray(s.enum) ? s.enum.map(String) : undefined;
  if (enumValues?.length) {
    const shown = enumValues.slice(0, 8).join(', ');
    const more = enumValues.length > 8 ? `, … (${enumValues.length})` : '';
    return `${typeOf(doc, s)} · one of: ${shown}${more}`;
  }
  const choice = (s.oneOf ?? s.anyOf) as unknown[] | undefined;
  if (Array.isArray(choice) && choice.length) {
    return `one of: ${choice.map(part => typeOf(doc, part)).join(' | ')}`;
  }
  if (s.additionalProperties) return 'object with any keys';
  return typeOf(doc, s);
}

function formatOf(doc: Json, source: string): string | undefined {
  const openapi = sourceTextOf(source, 'openapi', doc.openapi);
  if (openapi) return `OpenAPI ${openapi.split('.').slice(0, 2).join('.')}`;
  const swagger = sourceTextOf(source, 'swagger', doc.swagger);
  if (swagger) return `Swagger ${swagger}`;
  return undefined;
}

/** The first server: its variables at their defaults, or Swagger 2's host. */
function serverOf(doc: Json): string | undefined {
  const servers = Array.isArray(doc.servers) ? doc.servers : [];
  const first = servers[0];
  if (isObject(first) && typeof first.url === 'string') {
    const variables = isObject(first.variables) ? first.variables : {};
    return first.url.replace(/\{([^}]+)\}/g, (whole, name: string) => {
      const variable = variables[name];
      const fallback =
        isObject(variable) && textOf(variable.default) !== undefined
          ? textOf(variable.default)
          : undefined;
      return fallback ?? whole;
    });
  }
  if (typeof doc.host === 'string') {
    const schemes = Array.isArray(doc.schemes) ? doc.schemes : [];
    const scheme = schemes.includes('https')
      ? 'https'
      : (schemes[0] ?? 'https');
    const basePath = typeof doc.basePath === 'string' ? doc.basePath : '';
    return `${scheme}://${doc.host}${basePath}`;
  }
  return undefined;
}

function readSpec(doc: Json, source: string): ApiSpec | undefined {
  const format = formatOf(doc, source);
  if (!format) return undefined;

  const info = isObject(doc.info) ? doc.info : {};
  const components = isObject(doc.components) ? doc.components : {};
  // OpenAPI 3 keeps them under components, Swagger 2 at the top.
  let declared: Json = {};
  if (isObject(components.securitySchemes)) {
    declared = components.securitySchemes;
  } else if (isObject(doc.securityDefinitions)) {
    declared = doc.securityDefinitions;
  }
  const schemes = Object.fromEntries(
    Object.entries(declared).map(([name, raw]) => [name, schemeOf(name, raw)]),
  );

  const paths = isObject(doc.paths) ? doc.paths : {};
  const raw = new Map<Operation, Json>();
  const operations: Operation[] = Object.entries(paths).flatMap(
    ([path, pathItem]) => {
      const item = resolve(doc, pathItem);
      if (!isObject(item)) return [];
      return METHODS.flatMap(method => {
        const op = item[method];
        if (!isObject(op)) return [];
        const operation: Operation = {
          method: method.toUpperCase(),
          path,
          summary: typeof op.summary === 'string' ? op.summary : undefined,
          tags: Array.isArray(op.tags)
            ? op.tags.filter((t): t is string => typeof t === 'string')
            : [],
          requirement: firstRequirement(op.security),
          securityNames: namesIn(op.security),
          parameters: parametersOf(doc, item, op),
          hasBody: hasBodyOf(doc, op),
        };
        raw.set(operation, op);
        return [operation];
      });
    },
  );

  // The body is worked out for the one operation the sample is for: building
  // one for each of four hundred operations would cost the tab its opening.
  const sample = sampleOperationOf(
    operations,
    (firstRequirement(doc.security) ?? []).length > 0,
  );
  if (sample) sample.body = bodyOf(doc, raw.get(sample) ?? {});

  return {
    format,
    title: textOf(info.title),
    version: sourceTextOf(source, 'version', info.version),
    server: serverOf(doc),
    ...authOf(doc, schemes, operations),
    schemes,
    requirement: firstRequirement(doc.security),
    operations,
    schemas: schemasOf(doc),
    sample,
  };
}

/** Everything the tab shows about one API, or undefined if it is no OpenAPI. */
export function specOf(definition: unknown): ApiSpec | undefined {
  const doc = readDefinition(definition);
  if (!doc) return undefined;
  try {
    return readSpec(doc, definition as string);
  } catch {
    // A definition built to be read by nothing — a recursive alias, a shape
    // no contract has — reads as one that could not be read.
    return undefined;
  }
}

/** The operations grouped by their first tag, in the order the tags appear. */
export function capabilitiesOf(
  operations: Operation[],
): { tag?: string; operations: Operation[] }[] {
  const groups = new Map<string | undefined, Operation[]>();
  for (const op of operations) {
    const tag = op.tags[0];
    groups.set(tag, [...(groups.get(tag) ?? []), op]);
  }
  // Operations with no tag are what the definition did not sort; they follow
  // the ones it did.
  return [...groups.entries()]
    .sort(([a], [b]) => Number(a === undefined) - Number(b === undefined))
    .map(([tag, ops]) => ({ tag, operations: ops }));
}

// Words that end in a full stop without ending the sentence.
const ABBREVIATION = /(?:^|\s)(?:e\.g|i\.e|etc|vs|cf|incl|approx|resp)\.$/i;

/** Up to the first full stop, question or exclamation mark that ends one. */
function sentenceOf(text: string): string {
  const end = /[.!?](?=\s|$)/g;
  for (let m = end.exec(text); m; m = end.exec(text)) {
    const upTo = text.slice(0, m.index + 1);
    if (!ABBREVIATION.test(upTo)) return upTo;
  }
  return text;
}

/** Markdown as one line of plain text: emphasis, inline code, links undone. */
export function plainTextOf(markdown: string): string {
  return (
    markdown
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/`([^`]*)`/g, '$1')
      // Emphasis, but not the underscores of an identifier like client_id.
      .replace(/(\*\*|\*|\b__)(\S(?:.*?\S)?)\1/g, '$2')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/^#+\s*/gm, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * The first sentence of a description, as plain text: many are written in
 * Markdown — emphasis, inline code, whole code blocks — and a row has room
 * for one line.
 */
export function firstSentenceOf(markdown: string): string {
  // The first paragraph, its wrapped lines joined again: the later paragraphs
  // are often options, lists or code, which run together once the breaks go.
  const [first = ''] = markdown
    .replace(/```[\s\S]*?```/g, '\n\n')
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean);
  // A first line ending on a colon introduces what follows; it is the summary.
  const [firstLine = ''] = first.split('\n');
  const plain = plainTextOf(firstLine.trim().endsWith(':') ? firstLine : first);
  const sentence = sentenceOf(plain);
  return sentence.length > 160 ? `${sentence.slice(0, 157)}…` : sentence;
}
